<template><div><h1 id="jobs" tabindex="-1"><a class="header-anchor" href="#jobs"><span>Jobs</span></a></h1>
<p>Jobs — Zeigt und steuert die Batch Prozesse</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Unter Unix ist es möglich, Verarbeitungsschritte (Jobs, Prozesse) an das System zu übergeben, damit der Benutzer ohne Verzögerung weiterarbeiten kann. Als Verarbeitungsschritte können gelten: Auswertungen starten, Filter starten, Rechnungen drucken, Fensterinhalt drucken (Hardcopy), etc. Vor allem für länger dauernde Verarbeitungsschritte lohnt sich die Definition eines Jobs.</p>
<p>Ausserdem können Jobs zeitlich terminiert werden und so bei auslastungsintensiven Prozessen die Verarbeitung in Randzeiten (über Nacht, während des Wochenendes gesteuert werden).</p>
<p>Die Einrichtung von periodischen Verarbeitungen ist ebenfalls möglich, so können tägliche, wöchentliche, monatliche oder jährliche Verarbeitungsschritte im System fix hinterlegt werden.</p>
<p>Die Jobs werden direkt in den jeweiligen Programmen erfasst. Dieses Fenster erleichtert lediglich die Planung und Pflege von Jobs, sowohl von ad hoc aufgegebenen Prozessen wie auch von regelmässig laufenden Jobs. Die erfassten Jobs bleiben als Protokolle der Aktivitäten im System erhalten.</p>
<p>Pfad: <code v-pre>Systemverwaltung → Jobs</code></p>
<h2 id="datenfelder-listen-ansicht" tabindex="-1"><a class="header-anchor" href="#datenfelder-listen-ansicht"><span>Datenfelder (Listen Ansicht)</span></a></h2>
<dl>
<dt>Status</dt>
<dd>Die folgende Tabelle listet die möglichen Status auf:
<table>
<thead>
<tr>
<th>Status</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>versäumter Job</td>
<td>Der Job konnte nicht ausgeführt werden, kann z.B. auftreten, wenn während der vorgesehenen Startzeit das System nicht läuft.</td>
</tr>
<tr>
<td>abgebrochen, fehlerhaft</td>
<td>Der Job wurde abgebrochen. Das heisst, dass der Job gestartet, aber nicht zum Schluss ausgeführt wurde. Die Ursache kann je nach Situation unterschiedlich sein. Z.B. eine falsche, nicht abgefangene Parametrisierung oder der Job wurde von Aussen abgebrochen.</td>
</tr>
<tr>
<td>abgeschlossen, fehlerhaft</td>
<td>Der Job wurde zwar abgeschlossen, während der Ausführung ist eine unwerwartete Meldung gekommen. Die Meldung muss nicht zwangsläufig ein Fehler sein, es kann auch eine Warnung, Benachrichtigung über zu wenig Platz... sein. Detail muss im Protokoll geprüft werden.</td>
</tr>
<tr>
<td>geplant</td>
<td>Der Job ist in der Warteschlange und wird zur definierten Zeit gestartet.</td>
</tr>
<tr>
<td>wartet auf Ausführung</td>
<td>Der Job wartet auf die Ausführung. Das bedeutet, dass der Job nach seinen Zeitangaben bereits laufen sollte aber noch auf das Ende eines Vorläuferjobs in der Verarbeitungskette wartet.</td>
</tr>
<tr>
<td>Ausführung gestartet</td>
<td>Die Ausführung des Jobs wurde gestartet.</td>
</tr>
<tr>
<td>läuft</td>
<td>Der Job resp. die Verarbeitung läuft.</td>
</tr>
<tr>
<td>abgeschlossen</td>
<td>Der Job wurde fehlerfrei beendet und ist damit abgeschlossen.</td>
</tr>
</tbody>
</table>
</dd>
<dt>Startzeit</dt>
<dd>Verarbeitungsdatum und Verarbeitungszeit des Jobs</dd>
<dt>Statusänderung</dt>
<dd>Zeitpunkt der Änderung des Status des Jobs, z.B. wenn der Job abgebrochen oder abgeschlossen wird.</dd>
<dt>Ausgabe</dt>
<dd>Entweder wird das Verarbeitungsprotokoll angezeigt oder die Daten in eine Datei ausgegeben, abhängig vom jeweilig gestarteten Programm.</dd>
<dt>Programm</dt>
<dd>System interner Name des Programms für die Ausführung des Jobs.</dd>
<dt>Programmbeschreibung</dt>
<dd>Beschreibung des Programms für die Ausführung des Jobs.</dd>
<dt>Verarbeitungskette</dt>
<dd>Die Verarbeitungskette steuert die aufgegebenen Jobs. Je nach Definition der Verarbeitungskette, kann eine serielle oder parallele Verarbeitung vorgenommen werden. Bei serieller Verarbeitung warten alle anderen Jobs mit der gleichen Verarbeitungskette auf das abschliessen des vorgänger Jobs (sie haben dann Status <span class="guilabel">geplant</span> oder <span class="guilabel">wartet auf Ausführung</span>). Bei paralleler Verarbeitung können mehrere Jobs der gleichen Verarbeitungskette miteinander laufen. Siehe im Detail <a class="link" href="ft.refdata.systblkette.html" title="Verarbeitungskette">Verarbeitungskette</a><br>Wird als Verarbeitungskette nichts eingegeben, dann startet der Job unabhängig von anderen Jobs.</dd>
</dl>
<div class="hint-container warning">
<p class="hint-container-title">Achtung</p>
<p>Bricht ein Job einer Verarbeitungskette mit einem Fehler ab (Status <span class="guilabel">abgebrochen, fehlerhaft</span> oder <span class="guilabel">abgeschlossen, fehlerhaft</span>), so wird die Verarbeitungskette je nach Definition der Verarbeitungskette entweder fortgesetzt, oder alle anderen Jobs der Verarbeitungskette warten und werden nicht verarbeitet. In einem solchen Fall kann die Verarbeitungskette mit der Funktion &quot;Restart Verarbeitungskette...&quot; wieder fortgesetzt werden.</p>
</div>
<dl>
<dt>Job-ID</dt>
<dd>Eindeutige Nummer zur Identifikation des Jobs.</dd>
</dl>
<h2 id="datenfelder-detail-ansicht-edit-modus" tabindex="-1"><a class="header-anchor" href="#datenfelder-detail-ansicht-edit-modus"><span>Datenfelder (Detail Ansicht / Edit-Modus)</span></a></h2>
<dl>
<dt>Startzeit</dt>
<dd>Verarbeitungsdatum und Verarbeitungszeit des Jobs</dd>
<dt>Job-ID</dt>
<dd>Eindeutige Nummer zur Identifikation des Jobs.</dd>
<dt>Status</dt>
<dd>Die folgende Tabelle listet die möglichen Status auf:
<table>
<thead>
<tr>
<th>Status</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>versäumter Job</td>
<td>Der Job konnte nicht ausgeführt werden, kann z.B. auftreten, wenn während der vorgesehenen Startzeit das System nicht läuft.</td>
</tr>
<tr>
<td>abgebrochen, fehlerhaft</td>
<td>Der Job wurde abgebrochen, z.B. falsche Eingabeparameter, benötigte Verarbeitungsdatei nicht verfügbar, etc.</td>
</tr>
<tr>
<td>abgeschlossen, fehlerhaft</td>
<td>Der Job wurde zwar abgeschlossen, aber könnte fehlerhaft sein, da während der Verarbeitung irgend ein Fehler aufgetrten ist.</td>
</tr>
<tr>
<td>geplant</td>
<td>Der Job ist in der Warteschlange und wird zur definierten Zeit gestartet.</td>
</tr>
<tr>
<td>wartet auf Ausführung</td>
<td>Der Job wartet auf die Ausführung. Das bedeutet, dass der Job nach seinen Zeitangaben bereits laufen sollte aber noch auf das Ende eines Vorläuferjobs in der Verarbeitungskette wartet.</td>
</tr>
<tr>
<td>Ausführung gestartet</td>
<td>Die Ausführung des Jobs wurde gestartet.</td>
</tr>
<tr>
<td>läuft</td>
<td>Der Job resp. die Verarbeitung läuft.</td>
</tr>
<tr>
<td>abgeschlossen</td>
<td>Der Job wurde fehlerfrei beendet und ist damit abgeschlossen.</td>
</tr>
</tbody>
</table>
</dd>
<dt>Intervall</dt>
<dd>Verarbeitungsintervall des Jobs. Dieser wird durch 4 Parameter bestimmt:
<ol>
<li>
<p>Intervall (Verarbeitungsintervall), die [Anzahl Periodizität], die bis zur nächsten Ausführung des Jobs verstreichen sollen, 2-stellig, numerisch</p>
</li>
<li>
<p>Einheit (Zeiteinheit), die [Periodizität] an sich wird dargestellt als:</p>
<table>
<thead>
<tr>
<th>Periodizität</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Arbeitstag</td>
<td>Arbeitstage, alle Tage die nicht als Feiertage/Festtage definiert sind.</td>
</tr>
<tr>
<td>Tag</td>
<td>Tage, inklusive Wochenende und Feiertage.</td>
</tr>
<tr>
<td>Monat</td>
<td>Monate</td>
</tr>
<tr>
<td>Jahr</td>
<td>Jahre</td>
</tr>
</tbody>
</table>
</li>
<li>
<p>ausführen am Wochentag, kann bei monatlichen Verarbeitungen der Ausführungstag definiert werden.</p>
</li>
<li>
<p>bis Datum, bis zum angegebenem Datum wird die Aufgabe ausgeführt - wenn dieses Feld leer bleibt, dann wird die Aufgabe solange ausgeführt, bis es manuell unterbrochen wird.</p>
</li>
</ol>
</dd>
<dt>Druckername</dt>
<dd>Druckername</dd>
<dt>Ausgabedatei</dt>
<dd>Ausgabedatei, entweder wird das Verarbeitungsprotokoll oder die Daten in diese Datei ausgegeben, abhängig vom jeweilig gestarteten Programm.</dd>
<dt>E-Mail</dt>
<dd>E-Mail Adresse für die Zustellung des Outputs per E-Mail.</dd>
<dt>Programm</dt>
<dd>System interner Name des Programms für die Ausführung des Jobs.</dd>
<dt>ohne Label</dt>
<dd>Beschreibung des Programms für die Ausführung des Jobs.</dd>
<dt>Verarbeitungskette</dt>
<dd>Die Verarbeitungskette steuert die aufgegebenen Jobs. Je nach Definition der Verarbeitungskette, kann eine serielle oder parallele Verarbeitung vorgenommen werden. Bei serieller Verarbeitung warten alle anderen Jobs mit der gleichen Verarbeitungskette auf das abschliessen des vorgänger Jobs (sie haben dann Status <span class="guilabel">geplant</span> oder <span class="guilabel">wartet auf Ausführung</span>). Bei paralleler Verarbeitung können mehrere Jobs der gleichen Verarbeitungskette miteinander laufen. Siehe im Detail <a class="link" href="ft.refdata.systblkette.html" title="Verarbeitungskette">Verarbeitungskette</a><br>Wird als Verarbeitungskette nichts eingegeben, dann startet der Job unabhängig von anderen Jobs.</dd>
</dl>
<div class="hint-container caution">
<p class="hint-container-title">Achtung</p>
<p>Bricht ein Job einer Verarbeitungskette mit einem Fehler ab (Status <span class="guilabel">abgebrochen, fehlerhaft</span> oder <span class="guilabel">abgeschlossen, fehlerhaft</span>), so wird die Verarbeitungskette je nach Definition der Verarbeitungskette entweder fortgesetzt, oder alle anderen Jobs der Verarbeitungskette warten und werden nicht verarbeitet. In einem solchen Fall kann die Verarbeitungskette mit der Funktion &quot;Restart Verarbeitungskette...&quot; wieder fortgesetzt werden.</p>
</div>
<dl>
<dt>Parameter</dt>
<dd>Anzeige der übrigen Parameter, die bei der Definition des Jobs im Anwendungsprogramm eingegeben wurden. Dient der näheren Bestimmung des Jobs. Anzeige von maximal 5 verschiedenen Parametern möglich.</dd>
<dt>Protokoll</dt>
<dd>Im Protokoll wird die Start- und Endzeit protokolliert, sowie auch alle Fehlermeldungen.</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<h3 id="drucken" tabindex="-1"><a class="header-anchor" href="#drucken"><span>Drucken...</span></a></h3>
<p>Druckt den Job aus.</p>
<h3 id="job-abbrechen" tabindex="-1"><a class="header-anchor" href="#job-abbrechen"><span>Job abbrechen...</span></a></h3>
<p>Bricht einen laufenden Job ab.</p>
<h3 id="restart-verarbeitungskette" tabindex="-1"><a class="header-anchor" href="#restart-verarbeitungskette"><span>Restart Verarbeitungskette...</span></a></h3>
<p>Setzt die Verarbeitungskette fort nach einem Abbruch z.B. infolge eines fehlerhaften Jobs.</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
</div></template>


